import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Target, Flame, Beef, Calendar, Zap, Award, Scale } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import MainLayout from "../components/MainLayout";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const WeeklyAnalysisPage = () => {
  const { getAuthHeaders, API, user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [predictionData, setPredictionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalysis();
  }, []);

  const fetchAnalysis = async () => {
    try {
      const [analysisRes, streakRes, predictionRes] = await Promise.all([
        axios.get(`${API}/analysis/weekly`, { headers: getAuthHeaders() }),
        axios.get(`${API}/stats/streak`, { headers: getAuthHeaders() }),
        axios.get(`${API}/stats/prediction`, { headers: getAuthHeaders() })
      ]);
      setAnalysis(analysisRes.data);
      setStreakData(streakRes.data);
      setPredictionData(predictionRes.data);
    } catch (error) {
      console.error("Error fetching analysis:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-muted-foreground">Analizando tu semana...</div>
        </div>
      </MainLayout>
    );
  }

  if (!analysis || analysis.summary.days_tracked === 0) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="font-heading text-2xl font-semibold mb-2">Sin datos suficientes</h2>
            <p className="text-muted-foreground">
              Necesitas registrar comidas durante varios días para ver el análisis semanal.
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const { summary, daily_breakdown, patterns, coach_feedback } = analysis;
  const goalAdherence = summary.goal_adherence;

  const getPatternIcon = (type) => {
    switch (type) {
      case "success": return <CheckCircle className="w-5 h-5 text-primary" />;
      case "warning": return <AlertTriangle className="w-5 h-5 text-accent" />;
      case "alert": return <AlertTriangle className="w-5 h-5 text-destructive" />;
      default: return <Target className="w-5 h-5 text-muted-foreground" />;
    }
  };

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="weekly-analysis-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Análisis Semanal</h1>
          <p className="text-muted-foreground">Resumen de los últimos 7 días</p>
        </div>

        {/* Coach Feedback */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-6 ${
            user?.coach_mode 
              ? "bg-gradient-to-r from-destructive/10 to-accent/10 border border-destructive/20" 
              : "bg-primary/5 border border-primary/20"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
              user?.coach_mode ? "bg-destructive/20" : "bg-primary/20"
            }`}>
              {user?.coach_mode ? (
                <AlertTriangle className="w-6 h-6 text-destructive" />
              ) : (
                <Target className="w-6 h-6 text-primary" />
              )}
            </div>
            <div>
              <h3 className="font-heading font-semibold mb-2">
                {user?.coach_mode ? "El Coach dice:" : "Resumen de la semana"}
              </h3>
              <p className={`${user?.coach_mode ? "text-foreground" : "text-muted-foreground"}`}>
                {coach_feedback}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Streak & Prediction Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Streak Card */}
          {streakData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-gradient-to-br from-accent/10 to-primary/10 border border-accent/20 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold">Racha</h3>
                  <p className="text-xs text-muted-foreground">Días cumpliendo objetivo</p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-mono text-4xl font-bold text-accent">{streakData.current_streak}</p>
                  <p className="text-xs text-muted-foreground">días consecutivos</p>
                </div>
                <div className="text-right space-y-1">
                  <p className="text-xs flex items-center gap-1 justify-end">
                    <Award className="w-3 h-3 text-primary" />
                    Mejor: {streakData.best_streak}
                  </p>
                  <p className="text-xs flex items-center gap-1 justify-end">
                    <Target className="w-3 h-3" />
                    Éxito: {streakData.success_rate}%
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Prediction Card */}
          {predictionData?.has_prediction && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`border rounded-2xl p-6 ${
                predictionData.direction === "déficit" 
                  ? "bg-gradient-to-br from-primary/10 to-green-500/10 border-primary/20"
                  : predictionData.direction === "superávit"
                    ? "bg-gradient-to-br from-chart-carbs/10 to-accent/10 border-chart-carbs/20"
                    : "bg-card border-border/50"
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  predictionData.direction === "déficit" ? "bg-primary/20" : 
                  predictionData.direction === "superávit" ? "bg-chart-carbs/20" : "bg-muted"
                }`}>
                  <Scale className={`w-5 h-5 ${
                    predictionData.direction === "déficit" ? "text-primary" : 
                    predictionData.direction === "superávit" ? "text-chart-carbs" : "text-muted-foreground"
                  }`} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold">Predicción</h3>
                  <p className="text-xs text-muted-foreground">Cambio en 3 meses</p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className={`font-mono text-3xl font-bold ${
                    predictionData.three_month_change_kg > 0 ? "text-chart-carbs" : "text-primary"
                  }`}>
                    {predictionData.three_month_change_kg > 0 ? "+" : ""}{predictionData.three_month_change_kg}kg
                  </p>
                  <p className="text-xs text-muted-foreground">proyectado</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">
                    {predictionData.current_weight}kg → {predictionData.projected_weight_3_months}kg
                  </p>
                  <p className={`text-xs ${
                    predictionData.daily_difference > 0 ? "text-chart-carbs" : "text-primary"
                  }`}>
                    {predictionData.daily_difference > 0 ? "+" : ""}{predictionData.daily_difference} kcal/día
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border/50 rounded-2xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground">Media calorías</span>
            </div>
            <p className="font-mono text-2xl font-bold">{summary.avg_calories}</p>
            <p className="text-xs text-muted-foreground">
              {summary.avg_calories > summary.calorie_goal ? (
                <span className="text-destructive flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  +{summary.avg_calories - summary.calorie_goal} sobre objetivo
                </span>
              ) : (
                <span className="text-primary flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" />
                  {summary.calorie_goal - summary.avg_calories} bajo objetivo
                </span>
              )}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-card border border-border/50 rounded-2xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <Beef className="w-5 h-5 text-chart-protein" />
              <span className="text-sm text-muted-foreground">Media proteína</span>
            </div>
            <p className="font-mono text-2xl font-bold">{summary.avg_protein}g</p>
            <p className="text-xs text-muted-foreground">
              Objetivo: {summary.protein_goal}g
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border/50 rounded-2xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Días registrados</span>
            </div>
            <p className="font-mono text-2xl font-bold">{summary.days_tracked}</p>
            <p className="text-xs text-muted-foreground">
              {summary.total_meals} comidas totales
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-card border border-border/50 rounded-2xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Adherencia</span>
            </div>
            <p className={`font-mono text-2xl font-bold ${
              goalAdherence >= 80 ? "text-primary" : 
              goalAdherence >= 60 ? "text-accent" : "text-destructive"
            }`}>
              {goalAdherence}%
            </p>
            <p className="text-xs text-muted-foreground">
              {goalAdherence >= 80 ? "Excelente" : goalAdherence >= 60 ? "Mejorable" : "Necesita atención"}
            </p>
          </motion.div>
        </div>

        {/* Weekly Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <h3 className="font-heading font-semibold mb-4">Calorías diarias</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily_breakdown}>
                <XAxis 
                  dataKey="day" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.75rem',
                    boxShadow: '0 4px 20px rgb(0,0,0,0.1)'
                  }}
                  formatter={(value, name) => {
                    const labels = { calories: 'Calorías', protein: 'Proteína', carbs: 'Carbos', fat: 'Grasa' };
                    return [`${Math.round(value)}${name === 'calories' ? ' kcal' : 'g'}`, labels[name] || name];
                  }}
                />
                <ReferenceLine 
                  y={summary.calorie_goal} 
                  stroke="hsl(var(--primary))" 
                  strokeDasharray="5 5"
                  label={{ value: 'Objetivo', fill: 'hsl(var(--primary))', fontSize: 12 }}
                />
                <Bar 
                  dataKey="calories" 
                  fill="hsl(var(--primary))" 
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Macros Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <h3 className="font-heading font-semibold mb-4">Macronutrientes por día</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily_breakdown}>
                <XAxis 
                  dataKey="day" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.75rem',
                    boxShadow: '0 4px 20px rgb(0,0,0,0.1)'
                  }}
                  formatter={(value, name) => {
                    const labels = { protein: 'Proteína', carbs: 'Carbos', fat: 'Grasa' };
                    return [`${Math.round(value)}g`, labels[name] || name];
                  }}
                />
                <Bar dataKey="protein" fill="#4A6741" radius={[4, 4, 0, 0]} />
                <Bar dataKey="carbs" fill="#E09F3E" radius={[4, 4, 0, 0]} />
                <Bar dataKey="fat" fill="#E07A5F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-chart-protein rounded-sm" />
              <span className="text-sm text-muted-foreground">Proteína</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-chart-carbs rounded-sm" />
              <span className="text-sm text-muted-foreground">Carbos</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-chart-fat rounded-sm" />
              <span className="text-sm text-muted-foreground">Grasa</span>
            </div>
          </div>
        </motion.div>

        {/* Patterns */}
        {patterns && patterns.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card border border-border/50 rounded-2xl p-6"
          >
            <h3 className="font-heading font-semibold mb-4">Patrones detectados</h3>
            <div className="space-y-3">
              {patterns.map((pattern, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-3 p-4 rounded-xl ${
                    pattern.type === "success" ? "bg-primary/5" :
                    pattern.type === "warning" ? "bg-accent/10" :
                    "bg-destructive/10"
                  }`}
                >
                  {getPatternIcon(pattern.type)}
                  <div>
                    <p className="font-medium">{pattern.pattern}</p>
                    <p className="text-sm text-muted-foreground">{pattern.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Daily Breakdown Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-card border border-border/50 rounded-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-border/50">
            <h3 className="font-heading font-semibold">Desglose diario</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Día</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Calorías</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Proteína</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Carbos</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Grasa</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Comidas</th>
                </tr>
              </thead>
              <tbody>
                {daily_breakdown.map((day, index) => (
                  <tr key={day.date} className="border-t border-border/50">
                    <td className="p-4 font-medium">{day.day} <span className="text-muted-foreground text-sm">({day.date.slice(5)})</span></td>
                    <td className={`p-4 text-right font-mono ${
                      day.calories > summary.calorie_goal ? "text-destructive" : "text-foreground"
                    }`}>
                      {Math.round(day.calories)}
                    </td>
                    <td className="p-4 text-right font-mono text-chart-protein">{Math.round(day.protein)}g</td>
                    <td className="p-4 text-right font-mono text-chart-carbs">{Math.round(day.carbs)}g</td>
                    <td className="p-4 text-right font-mono text-chart-fat">{Math.round(day.fat)}g</td>
                    <td className="p-4 text-right text-muted-foreground">{day.meals_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default WeeklyAnalysisPage;
